/**
 * LOT 500 — Composant SEO réutilisable pour chaque page publique.
 *
 * Usage :
 *   <SEOHead
 *     title="À propos — Wealth Management"
 *     description="..."
 *     canonical="https://www.wealth-management-assets.com/fr/a-propos"
 *     hreflangs={{ fr: '/fr/a-propos', en: '/en/about' }}
 *     noindex={false}
 *   />
 *
 * Notes :
 *  - title / description sont automatiquement préfixées si trop courtes
 *  - canonical doit être ABSOLU (Google et OpenAI exigent l'URL complète)
 *  - hreflangs : map { lang: pathRelatif } qui sera converti en URLs absolues
 *  - noindex=true → injecte <meta name="robots" content="noindex,nofollow">
 *
 * Le `<HelmetProvider>` racine est wrappé dans App.js.
 */
import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { usePageSeoRegistration } from './public/SeoOwnership';

const SITE_URL = 'https://www.wealth-management-assets.com';
const DEFAULT_TITLE = 'Wealth Management — Global Real Assets';
const DEFAULT_DESC = 'Plateforme privée de gestion de patrimoine immobilier : club deals, SCI, SPV. Réservée aux investisseurs qualifiés et family offices.';
const DEFAULT_OG_IMAGE = `${SITE_URL}/og-image.png`;

/**
 * PHASE 8 SEO FIX — retire du <head> les balises statiques de index.html
 * (marquées data-static-seo : canonical, OG, description homepage).
 * Sans ce nettoyage, chaque route dynamique expose DEUX canonicals et les
 * crawlers retiennent le premier (celui de la homepage). Les balises
 * statiques restent servies dans le HTML brut comme fallback pour les
 * scrapers sans JavaScript.
 */
export function stripStaticSeoTags() {
  if (typeof document === 'undefined') return;
  document.head.querySelectorAll('[data-static-seo]').forEach((el) => el.remove());
}

export default function SEOHead({
  title,
  description,
  canonical,
  hreflangs,
  ogImage,
  ogType = 'website',
  noindex = false,
  jsonLd,
  article,
  isLayoutDefault = false,
}) {
  // HARDENING V1 (LOT 4) — un SEOHead de PAGE s'enregistre comme propriétaire
  // du SEO de la route : PublicSeoDefaults (layout) se tait alors, garantissant
  // UNE seule série de balises dans le <head> (react-helmet-async ne déduplique
  // pas entre instances). Les défauts du layout ne s'enregistrent pas.
  usePageSeoRegistration(!isLayoutDefault);

  useEffect(() => {
    stripStaticSeoTags();
  }, []);

  const finalTitle = title
    ? (/Wealth Management|WM Global Real Assets/.test(title) ? title : `${title} · ${DEFAULT_TITLE}`)
    : `${DEFAULT_TITLE} · Investissement immobilier privé`;
  const finalDesc = description || DEFAULT_DESC;
  const finalOgImage = ogImage || DEFAULT_OG_IMAGE;
  const robotsContent = noindex
    ? 'noindex, nofollow'
    : 'index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1';

  return (
    <Helmet>
      <title>{finalTitle}</title>
      <meta name="description" content={finalDesc} />
      <meta name="robots" content={robotsContent} />
      {canonical && <link rel="canonical" href={canonical} />}

      {/* hreflang alternates */}
      {hreflangs && Object.entries(hreflangs).map(([lang, path]) => (
        <link key={lang} rel="alternate" hrefLang={lang} href={`${SITE_URL}${path}`} />
      ))}
      {hreflangs?.fr && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${hreflangs.fr}`} />
      )}

      {/* Open Graph */}
      <meta property="og:type" content={ogType} />
      <meta property="og:title" content={finalTitle} />
      <meta property="og:description" content={finalDesc} />
      <meta property="og:image" content={finalOgImage} />
      {canonical && <meta property="og:url" content={canonical} />}

      {/* Open Graph article:* (uniquement pour ogType="article") */}
      {ogType === 'article' && article?.publishedTime && (
        <meta property="article:published_time" content={article.publishedTime} />
      )}
      {ogType === 'article' && article?.modifiedTime && (
        <meta property="article:modified_time" content={article.modifiedTime} />
      )}
      {ogType === 'article' && article?.author && (
        <meta property="article:author" content={article.author} />
      )}
      {ogType === 'article' && article?.section && (
        <meta property="article:section" content={article.section} />
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={finalTitle} />
      <meta name="twitter:description" content={finalDesc} />
      <meta name="twitter:image" content={finalOgImage} />

      {/* JSON-LD optional — tableau accepté : UN <script> par bloc de schéma
          (Article + BreadcrumbList doivent apparaître comme 2 blocs distincts) */}
      {jsonLd && (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).map((block, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(block).replace(/</g, '\\u003c')}
        </script>
      ))}
    </Helmet>
  );
}
